<template>
  <div class="container mt-5">
    <!--h2 class="text-center mb-4">Livestock Management Dashboard</h2-->

    <!-- Year & Month Selection -->
    <div class="d-flex justify-content-center mb-3">
      <select v-model="selectedYear" class="form-select w-auto me-2" @change="fetchData">
        <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
      </select>
      <select v-model="selectedMonth" class="form-select w-auto" @change="fetchData">
        <option value="all">All Months</option>
        <option v-for="(month, index) in months" :key="index" :value="index + 1">{{ month }}</option>
      </select>
    </div>

    <!-- Summary Cards -->
    <div class="row text-center mb-4">
      <div class="col-md-4">
        <div class="card p-3 shadow-sm">
          <h5>Total Expenses</h5>
          <h3 class="text-danger">{{ formatPrice(totalExpenses) }}</h3>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card p-3 shadow-sm">
          <h5>Livestock Incoming</h5>
          <h3 class="text-success">{{ totalIncoming }}</h3>
          <p class="text-muted">Total Price: {{ formatPrice(totalIncomingPrix) }}</p> <!-- ✅ Added -->
        </div>
      </div>
      <div class="col-md-4">
        <div class="card p-3 shadow-sm">
          <h5>Livestock Outgoing</h5>
          <h3 class="text-warning">{{ totalOutgoing }}</h3>
          <p class="text-muted">Total Price: {{ formatPrice(totalOutgoingPrix) }}</p> <!-- ✅ Added -->
        </div>
      </div>
    </div>

    <!-- Charts Section -->
    <div class="row mb-4">
      <div class="col-md-6">
        <canvas ref="expenseChart"></canvas>
      </div>
      <div class="col-md-6">
        <canvas ref="livestockChart"></canvas>
      </div>
    </div>

    <!-- Expenses Table -->
    <div class="card">
      <table class="table table-striped text-center">
        <thead>
          <tr>
            <th>Category</th>
            <th>Amount (€)</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(expense, index) in expenses" :key="index">
            <td>{{ expense.name }}</td>
            <td>{{ formatPrice(expense.amount) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, nextTick, computed } from 'vue';
import Chart from 'chart.js/auto';
import { MonthlyExpensesService, MovementService } from '../api'; // Corrected import statement
import { useI18n } from 'vue-i18n';

export default {
  setup() {
    const { t } = useI18n();
    const selectedYear = ref(new Date().getFullYear());
    const selectedMonth = ref('all');
    const totalExpenses = ref(0);
    const totalIncoming = ref(0);
    const totalOutgoing = ref(0);
    const totalIncomingPrix = ref(0);
    const totalOutgoingPrix = ref(0);
    const expenses = ref([]);

    const months = computed(() => [
      t("months.january"), t("months.february"), t("months.march"), t("months.april"),
      t("months.may"), t("months.june"), t("months.july"), t("months.august"),
      t("months.september"), t("months.october"), t("months.november"), t("months.december")
    ]);

    const availableYears = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);
    const expenseChart = ref(null);
    let expenseChartInstance = null; // Store the chart instance to destroy it later
    const livestockChart = ref(null);

    const formatPrice = (value) => `€${value.toFixed(2)}`;
    
    const fetchData = async () => {
      try {
        const params = {};
        params.year = selectedYear.value ? selectedYear.value : '';
        params.month = selectedMonth.value !== 'all' ? selectedMonth.value : '';
        const response = await MonthlyExpensesService.getExpenseSummary(params);
        expenses.value = response;
        totalExpenses.value = expenses.value.reduce((sum, expense) => sum + expense.amount, 0);

        //fetch sum movement
        const responseMovement = await MovementService.getMovementSummary()
        console.log('responseMovement ' + JSON.stringify(responseMovement))
        totalIncoming.value = responseMovement.incoming
        totalOutgoing.value = responseMovement.outgoing
        totalIncomingPrix.value = responseMovement.incomingPrix
        totalOutgoingPrix.value = responseMovement.outgoingPrix

        await nextTick(); // Ensure DOM updates before rendering the chart
        renderExpenseChart(); // Call function to update the chart
        renderLivestockChart(); // Render livestock chart
      } catch (error) {
        console.error('Error fetching expenses:', error);
      }      
    };

    const renderExpenseChart = () => {
      // Destroy previous chart instance before creating a new one
      if (expenseChartInstance) {
        expenseChartInstance.destroy();
      }

      if (expenseChart.value) {
        expenseChartInstance = new Chart(expenseChart.value, {
          type: 'bar',
          data: {
            labels: expenses.value.map(e => e.name), // Changed category to name
            datasets: [{
              label: 'Expenses (€)',
              data: expenses.value.map(e => e.amount),
              backgroundColor: 'rgba(255, 99, 132, 0.5)'
            }]
          }
        });
      }
    };

    const renderLivestockChart = () => {
      if (livestockChart.value) {
        new Chart(livestockChart.value, {
          type: 'line',
          data: {
            labels: ['Pen A', 'Pen B', 'Pen C', 'Pen D', 'Pen E'],
            datasets: [{
              label: 'Livestock Movement',
              data: [100, 200, 30, 70, 100],
              borderColor: 'rgba(54, 162, 235, 1)',
              fill: false
            }]
          }
        });
      }
    };

    onMounted(() => {
      fetchData();
    });

    return {
      selectedYear,
      selectedMonth,
      totalExpenses,
      totalIncoming,
      totalOutgoing,
      totalIncomingPrix,
      totalOutgoingPrix,
      expenses,
      months,
      availableYears,
      fetchData,
      formatPrice,
      expenseChart,
      livestockChart
    };
  }
};
</script>

<style scoped>
.card {
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}
.table td, .table th {
  padding: 8px;
}
</style>
